import { ReactComponent as OutlineChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { useSessionTypeEditor } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/session-type-editor-context/SessionTypeEditorContext";
import ConfirmModal from "components/confirm-modal";
import ErrorMessage from "components/error-message";
import IconButton from "components/icon-button";
import SessionTypesUI from "components/session-types-ui/SessionTypesUI";
import { useSessionTypes } from "contexts/SessionTypesContext";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import postSessionTypes from "services/scheduler/postAppointmentTypes";
import { hideMobileSidebar } from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import styles from "./SessionTypesMobileScreen.module.scss";

const ANALYTICS_COMPONENT_NAME = "edit-session-types-mobile-screen";

const SessionTypesMobileScreen = () => {
  const dispatch = useDispatch();

  const { openSessionTypeEditor } = useSessionTypeEditor();
  const { sessionTypes, setSessionTypes } = useSessionTypes();

  const [error, setError] = useState<any>();

  const {
    show: showConfirmDeletion,
    open: openConfirmDeletion,
    close: closeConfirmDeletion,
  } = useToggleModal(false);

  const [sessionTypeToDelete, setSessionTypeToDelete] = useState<
    SessionType | undefined
  >();

  const handleOpenConfirmDeletion = useCallback(
    (sessionType: SessionType) => {
      setSessionTypeToDelete(sessionType);
      openConfirmDeletion();
    },
    [openConfirmDeletion],
  );

  const saveChanges = useCallback(async (sessionTypes: SessionType[]) => {
    try {
      await postSessionTypes(sessionTypes);
      setError(undefined);
    } catch (error) {
      setError(error);
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton onClick={() => dispatch(hideMobileSidebar())}>
          <OutlineChevronLeft />
        </IconButton>
        My Session Types
        <div style={{ visibility: "hidden" }}>
          <IconButton>
            <OutlineChevronLeft />
          </IconButton>
        </div>
      </div>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}
      <div className={styles.content}>
        <SessionTypesUI
          sessionTypes={sessionTypes}
          onApply={(updatedSessionType, i) =>
            setSessionTypes((previousValue) => {
              const nextValue = [
                ...previousValue.slice(0, i),
                updatedSessionType,
                ...previousValue.slice(i + 1),
              ];
              saveChanges(nextValue);
              return nextValue;
            })
          }
          onEdit={(v, i) => {
            track(
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              [ANALYTICS_COMPONENT_NAME, "edit-button"],
              "click",
            );

            openSessionTypeEditor(
              {
                getIsLabelInvalid: (label: string | undefined) =>
                  !label
                    ? false
                    : sessionTypes.some((el) => el.label === label),
                onApply: (updatedSessionType: SessionType) => {
                  setSessionTypes((previousValue) => {
                    const nextValue = [
                      ...previousValue.slice(0, i),
                      updatedSessionType,
                      ...previousValue.slice(i + 1),
                    ];
                    saveChanges(nextValue);
                    return nextValue;
                  });
                },
                onDelete: (sessionType: SessionType) => {
                  track(
                    PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                    [ANALYTICS_COMPONENT_NAME, "delete-button"],
                    "click",
                  );

                  handleOpenConfirmDeletion(sessionType);
                },
              },
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              v,
            );
          }}
          onDelete={(sessionType: SessionType) => {
            track(
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              [ANALYTICS_COMPONENT_NAME, "delete-button"],
              "click",
            );

            handleOpenConfirmDeletion(sessionType);
          }}
          setSessionTypes={(
            nextState:
              | SessionType[]
              | ((prevValue: SessionType[]) => SessionType[]),
          ) => {
            if (typeof nextState === "function") {
              setSessionTypes((prevValue) => {
                const nextValue = nextState(prevValue);
                saveChanges(nextValue);
                return nextValue;
              });
              return;
            }

            saveChanges(nextState);
            setSessionTypes(nextState);
          }}
          variant="mobile"
        />

        <ConfirmModal
          labelTitle={messages.deleteSessionType}
          children={messages.theSessionInformationWillNotBeSaved}
          labelConfirm={messages.delete}
          show={showConfirmDeletion}
          onCancel={() => {
            track(
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              [ANALYTICS_COMPONENT_NAME, "confirm-modal-cancel"],
              "click",
            );
            closeConfirmDeletion();
          }}
          onConfirm={() => {
            track(
              PRO_CALENDAR_ANALYTICS_PAGE_NAME,
              [ANALYTICS_COMPONENT_NAME, "confirm-modal-confirm"],
              "click",
            );

            if (!sessionTypeToDelete) {
              closeConfirmDeletion();
              return;
            }

            setSessionTypes((previousValue) => {
              const itemId = previousValue.findIndex(
                (el) => el?.label === sessionTypeToDelete.label,
              );
              const nextValue = [
                ...previousValue.slice(0, itemId),
                ...previousValue.slice(itemId + 1),
              ];
              saveChanges(nextValue);
              return nextValue;
            });

            setSessionTypeToDelete(undefined);
            closeConfirmDeletion();
          }}
        />
      </div>
      <button
        className={styles["add-button"]}
        onClick={() => {
          openSessionTypeEditor(
            {
              getIsLabelInvalid: (label: string | undefined) =>
                !label ? false : sessionTypes.some((el) => el.label === label),
              onApply: (addedSessionType: SessionType) =>
                setSessionTypes((previousValue) => {
                  const nextValue = [...previousValue, addedSessionType];
                  saveChanges(nextValue);
                  return nextValue;
                }),
            },
            PRO_CALENDAR_ANALYTICS_PAGE_NAME,
          );
        }}
      >
        <OutlinePlus color="white" />
      </button>
    </div>
  );
};

export default SessionTypesMobileScreen;
